// API client for Phase 6.1 Platform Economics + Phase 6.4 Cost Entry role.
// Accessible to platform admins AND users in the new "Cost Entry Staff"
// Django group. Backend enforces with IsCostEntryStaff.
//
// Backend routes (see backend/organizations/views.py):
//   GET    /api/organizations/platform-costs/                  list + filter
//   POST   /api/organizations/platform-costs/                  create
//   PATCH  /api/organizations/platform-costs/{id}/             edit
//   DELETE /api/organizations/platform-costs/{id}/             remove
//   GET    /api/organizations/platform-costs/summary/          totals by category
//   GET    /api/organizations/platform-assumptions/            singleton rates row
//   PATCH  /api/organizations/platform-assumptions/{id}/       update rates
//   GET    /api/organizations/cost-categories/                 list dynamic categories
//   POST   /api/organizations/cost-categories/                 create new category
//   PATCH  /api/organizations/cost-categories/{id}/            edit category
import { api } from './client.js'

// ---------- Cost CRUD ----------

export async function listPlatformCosts(params = {}) {
  // Supported params: category, organization, from (YYYY-MM-DD), to, page, ordering
  const { data } = await api.get('/api/organizations/platform-costs/', { params })
  return data
}

export async function getPlatformCostsSummary(params = {}) {
  // Same filter params as the list endpoint (excluding pagination).
  const { data } = await api.get(
    '/api/organizations/platform-costs/summary/', { params },
  )
  return data
}

export async function createPlatformCost(payload) {
  // payload: { incurred_on, category, amount_pgk, hours?, notes?, organization? }
  const { data } = await api.post('/api/organizations/platform-costs/', payload)
  return data
}

export async function updatePlatformCost(id, patch) {
  const { data } = await api.patch(
    `/api/organizations/platform-costs/${id}/`, patch,
  )
  return data
}

export async function deletePlatformCost(id) {
  await api.delete(`/api/organizations/platform-costs/${id}/`)
}

// ---------- Rates (PlatformAssumption singleton) ----------

export async function getPlatformAssumptions() {
  // Returns a 1-element results list (or array, depending on pagination
  // shape). Caller picks `.results[0]` or `[0]`.
  const { data } = await api.get('/api/organizations/platform-assumptions/')
  return data
}

export async function updatePlatformAssumptions(id, patch) {
  const { data } = await api.patch(
    `/api/organizations/platform-assumptions/${id}/`, patch,
  )
  return data
}

// ---------- CostCategory CRUD (Phase 6.4 — dynamic categories) ----------

export async function listCostCategories({ includeInactive = false } = {}) {
  const params = includeInactive ? { include_inactive: 1 } : {}
  const { data } = await api.get(
    '/api/organizations/cost-categories/', { params },
  )
  // Endpoint is non-paginated → returns a plain array.
  return Array.isArray(data) ? data : (data.results || [])
}

export async function createCostCategory({ name, description = '', sort_order = 100 }) {
  // Slug is auto-derived from name on the server when omitted.
  const { data } = await api.post(
    '/api/organizations/cost-categories/',
    { name, description, sort_order },
  )
  return data
}

export async function updateCostCategory(id, patch) {
  const { data } = await api.patch(
    `/api/organizations/cost-categories/${id}/`, patch,
  )
  return data
}

// ---------- Category metadata for the FE ----------
//
// The list itself is fetched from the API (see listCostCategories
// above). These small constants encode the metadata the API doesn't
// carry: which slugs are time-based (require an hours input + auto-
// fill amount from a rate), and which PlatformAssumption field
// supplies that rate. New user-added categories default to NOT
// time-based; only the two seeded slugs below behave that way.

export const TIME_BASED_CATEGORY_SLUGS = new Set([
  'clinic_time_foregone',
  'opportunity_cost',
])

export const RATE_FIELD_FOR_SLUG = {
  clinic_time_foregone: 'doctor_clinic_hourly_rate_pgk',
  opportunity_cost:     'doctor_hospital_hourly_rate_pgk',
}

export function isTimeBasedCategory(slug) {
  return TIME_BASED_CATEGORY_SLUGS.has(slug)
}

// Legacy helper kept for backwards compat — callers should prefer
// reading category.name from the API response.
export function categoryLabel(slugOrName) {
  return slugOrName
}

// Legacy export kept as an empty array so any module that still imports
// it doesn't crash. The real list now comes from listCostCategories().
export const PLATFORM_COST_CATEGORIES = []
